import { useCallback, useEffect, useState } from 'react';
import { hojeBrasilia, lerPagamentos } from '../../central';
import type { PagamentosDoPeriodo } from '../../central';
import { EstadoVazio, ErroDeArea } from '../../design/Estados';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { CORES } from '../../design/graficos/Grafico';
import { MODELOS_EMPILHADO, empilhado, fmt } from '../../design/graficos/modelos';
import { Icone } from '../../design/Icone';
import { BarraDeFiltros, Kpi, diaCurto, lerFiltros, textoDoPeriodo } from '../paineis/pecasDaVisao';
import { SubnavDeVendas } from './SubnavDeVendas';
import './central.css';

/**
 * VENDAS › PAGAMENTOS (dono, 06/10/2026) — as formas de pagamento que o caixa do HOS registrou no
 * período: dinheiro, PIX, débito, crédito (à vista e parcelado), cartão sem tipo, crediário, etc.
 * O dado é do caixa, lido do coletor do HOS dia a dia. Dia ainda não lido aparece como aviso —
 * nunca como zero. Se a soma das formas não fecha com o total do HOS, a tela mostra a diferença.
 * "Cartão sem tipo" é a maquininha fora do TEF: o HOS não registra se foi débito ou crédito, e
 * a tela não adivinha.
 */

type Forma = PagamentosDoPeriodo['formas'][number];

const brl = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pctTxt = (p: number | null) => (p === null ? '—' : fmt(p, 'pct'));
const parcelasTxt = (n: number) => (n === 1 ? 'À vista (1x)' : `${String(n)}x`);

/** Nome curto de cada forma para as legendas (o rótulo do HOS é longo em algumas). */
const NOME_CURTO: Readonly<Record<string, string>> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  debito: 'Débito',
  credito: 'Crédito',
  cartao_sem_tipo: 'Cartão sem tipo',
  cartao_sem_detalhe: 'Cartão sem detalhe',
  crediario: 'Crediário',
  convenio: 'Convênio',
  cheque: 'Cheque',
  credito_cliente: 'Crédito do cliente',
  farmacia_popular: 'Farmácia Popular',
  pbm: 'PBM',
  site: 'Pedido do site',
  transferencia: 'Transferência',
  deposito: 'Depósito',
  outros: 'Outros',
};
const nomeDe = (f: { forma: string; rotulo: string }) => NOME_CURTO[f.forma] ?? f.rotulo;

export function PagamentosDoCaixa({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  // padrão desta tela: o mês (um dia só diz pouco sobre como o cliente paga)
  const qs = new URLSearchParams(parametros);
  if (!qs.get('p') && !qs.get('dia')) qs.set('p', 'mes');
  const f = lerFiltros(qs, hoje);
  const { de, ate } = f.periodo;
  const [d, setD] = useState<PagamentosDoPeriodo | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerPagamentos(de, ate).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [de, ate]);
  useEffect(carregar, [carregar]);

  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const total = d?.somaCentavos ?? 0;
  const forma = (k: string) => d?.formas.find((x) => x.forma === k);
  const semNada = d !== null && d.diasComDado === 0;

  const cartao = (k: string, rotulo: string, icone: 'money' | 'scan' | 'receipt' | 'grid') => {
    const x = forma(k);
    return (
      <Kpi
        rotulo={`${rotulo} · ${quando}`}
        icone={icone}
        valor={d ? (semNada ? '—' : brl(x?.valorCentavos ?? 0)) : null}
        variacao={null}
        sub={
          d
            ? semNada
              ? 'sem dado de pagamento'
              : `${pctTxt(x?.pct ?? (total > 0 ? 0 : null))} do total · ${fmt(x?.cupons ?? 0, 'inteiro')} cupons`
            : null
        }
        link="#/pagamentos"
        destaque={k === 'dinheiro'}
      />
    );
  };

  return (
    <section className="cv">
      <SubnavDeVendas atual="pagamentos" direita="caixa do HOS" />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Formas de pagamento</h1>
            <div className="vg-contexto">O que o caixa registrou · {quando}</div>
          </div>
        </header>

        <BarraDeFiltros f={f} hoje={hoje} rota="pagamentos" padrao="mes" />

        {erro ? (
          <ErroDeArea oQue="as formas de pagamento" aoTentar={carregar} />
        ) : (
          <>
            {d && d.diasSemDado.length > 0 && !semNada && (
              <div className="cv-aviso" role="status">
                <Icone nome="warn" tamanho={15} traco={1.6} />
                <span>
                  Sem dado de pagamento para {fmt(d.diasSemDado.length, 'inteiro')}{' '}
                  {d.diasSemDado.length === 1 ? 'dia' : 'dias'} do período (
                  {resumoDosDias(d.diasSemDado)}). Os números abaixo cobrem só os{' '}
                  {fmt(d.diasComDado, 'inteiro')} dias lidos.
                </span>
              </div>
            )}
            {d && d.diferencaCentavos !== 0 && (
              <div className="cv-aviso" role="alert">
                <Icone nome="warn" tamanho={15} traco={1.6} />
                <span>
                  A soma das formas ({brl(d.somaCentavos)}) não fecha com o total do HOS (
                  {brl(d.totalCentavos)}): diferença de {brl(d.diferencaCentavos)}. Os valores
                  mostrados são os do caixa, sem ajuste.
                </span>
              </div>
            )}

            {semNada ? (
              <div className="vg-bloco">
                <EstadoVazio
                  icone="receipt"
                  titulo="Sem dado de pagamento neste período"
                  texto="O InovaPay lê as formas de pagamento do caixa do HOS dia a dia. Nenhum dia deste período foi lido ainda."
                />
              </div>
            ) : (
              <>
                <div className="vg-kpis cv-kpis-5">
                  {cartao('dinheiro', 'Dinheiro', 'money')}
                  {cartao('pix', 'PIX', 'scan')}
                  {cartao('debito', 'Débito', 'receipt')}
                  {cartao('credito', 'Crédito', 'grid')}
                </div>

                <div className="vg-bloco cv-pg-composicao">
                  <div className="vg-bloco-topo">
                    <div>
                      <h2>Como o cliente pagou</h2>
                      <p>
                        {d
                          ? `${brl(d.somaCentavos)} em ${fmt(d.cupons, 'inteiro')} cupons`
                          : 'carregando…'}
                      </p>
                    </div>
                  </div>
                  {d ? <BarraDeComposicao d={d} /> : <div className="vg-lista-osso" />}
                </div>

                <div className="vg-bloco cv-lista">
                  <div className="vg-bloco-topo">
                    <div>
                      <h2>Por forma de pagamento</h2>
                      <p>valor, participação, cupons e ticket médio de cada forma</p>
                    </div>
                  </div>
                  {!d ? (
                    <div className="vg-lista-osso" aria-busy="true">
                      {[0, 1, 2, 3, 4].map((k) => (
                        <i key={k} />
                      ))}
                    </div>
                  ) : (
                    <div className="cv-rolagem">
                      <table className="cv-tabela">
                        <thead>
                          <tr>
                            <th>Forma</th>
                            <th className="cv-dir">Valor</th>
                            <th className="cv-dir">% do total</th>
                            <th className="cv-dir">Cupons</th>
                            <th className="cv-dir">Ticket médio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.formas.map((x) => (
                            <LinhaDaForma key={x.forma} x={x} />
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td className="cv-forte">Total das formas</td>
                            <td className="cv-dir cv-forte">{brl(d.somaCentavos)}</td>
                            <td className="cv-dir">{d.somaCentavos ? '100,0%' : '—'}</td>
                            <td className="cv-dir">{fmt(d.cupons, 'inteiro')}</td>
                            <td className="cv-dir">
                              {d.cupons ? brl(Math.round(d.somaCentavos / d.cupons)) : '—'}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {d && <CreditoParcelado d={d} />}
                {d && <PorDia d={d} />}

                <div className="cv-nota">
                  <p>
                    <strong>Cartão sem tipo</strong> = maquininha fora do TEF: o HOS não registra se
                    foi débito ou crédito. Por isso ele aparece separado, e não dentro de Débito ou
                    Crédito.
                  </p>
                  <p>
                    <strong>Cartão sem detalhe</strong> é a diferença entre o caixa e a maquininha e
                    pode ser negativa. Um cupom pago em duas formas conta nas duas (a coluna Cupons
                    não soma ao total de cupons).
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/** "03/10, 04/10 e mais 5" — nunca uma lista de 30 datas. */
function resumoDosDias(dias: readonly string[]): string {
  const vistos = dias.slice(0, 4).map(diaCurto).join(', ');
  return dias.length > 4 ? `${vistos} e mais ${String(dias.length - 4)}` : vistos;
}

function LinhaDaForma({ x }: { x: Forma }) {
  return (
    <tr>
      <td className="cv-forte">{nomeDe(x)}</td>
      <td className={`cv-dir cv-forte${x.valorCentavos < 0 ? ' cv-neg' : ''}`}>
        {brl(x.valorCentavos)}
      </td>
      <td className="cv-dir">{pctTxt(x.pct)}</td>
      <td className="cv-dir">{fmt(x.cupons, 'inteiro')}</td>
      <td className="cv-dir">{x.ticketCentavos === null ? '—' : brl(x.ticketCentavos)}</td>
    </tr>
  );
}

/** A barra horizontal de composição: uma fatia por forma com valor positivo, na ordem do valor. */
function BarraDeComposicao({ d }: { d: PagamentosDoPeriodo }) {
  const fatias = d.formas.filter((x) => x.valorCentavos > 0);
  const base = fatias.reduce((s, x) => s + x.valorCentavos, 0);
  const negativas = d.formas.filter((x) => x.valorCentavos < 0);
  return (
    <div className="cv-pg-barra-caixa">
      <div className="cv-pg-barra" role="img" aria-label="Composição das formas de pagamento">
        {fatias.map((x, i) => (
          <span
            key={x.forma}
            className="cv-pg-fatia"
            style={{
              width: `${String((x.valorCentavos / base) * 100)}%`,
              background: CORES[i % CORES.length] ?? '#8e959b',
            }}
            title={`${nomeDe(x)}: ${brl(x.valorCentavos)} (${pctTxt(x.pct)})`}
          />
        ))}
      </div>
      <ul className="cv-pg-legenda">
        {fatias.map((x, i) => (
          <li key={x.forma}>
            <i style={{ background: CORES[i % CORES.length] ?? '#8e959b' }} />
            <span>{nomeDe(x)}</span>
            <strong>{pctTxt(x.pct)}</strong>
          </li>
        ))}
      </ul>
      {negativas.length > 0 && (
        <p className="cv-pg-obs">
          Fora da barra (valor negativo):{' '}
          {negativas.map((x) => `${nomeDe(x)} ${brl(x.valorCentavos)}`).join(' · ')}.
        </p>
      )}
    </div>
  );
}

function CreditoParcelado({ d }: { d: PagamentosDoPeriodo }) {
  const c = d.credito;
  return (
    <div className="vg-bloco cv-lista">
      <div className="vg-bloco-topo">
        <div>
          <h2>Crédito parcelado</h2>
          <p>
            {c.valorCentavos > 0
              ? `${brl(c.valorCentavos)} no crédito · ${pctTxt(c.pctParcelado)} parcelado · parcela média ${
                  c.parcelaMedia === null ? '—' : fmt(c.parcelaMedia, 'decimal')
                }x (ponderada pelo valor)`
              : 'nenhum pagamento no crédito identificado no período'}
          </p>
        </div>
      </div>
      {c.linhas.length > 0 && (
        <div className="cv-rolagem">
          <table className="cv-tabela">
            <thead>
              <tr>
                <th>Parcelas</th>
                <th className="cv-dir">Valor</th>
                <th className="cv-dir">% do crédito</th>
                <th className="cv-dir">Cupons</th>
              </tr>
            </thead>
            <tbody>
              {c.linhas.map((l) => (
                <tr key={l.parcelas}>
                  <td className="cv-forte">{parcelasTxt(l.parcelas)}</td>
                  <td className="cv-dir cv-forte">{brl(l.valorCentavos)}</td>
                  <td className="cv-dir">{pctTxt(l.pctDoCredito)}</td>
                  <td className="cv-dir">{fmt(l.cupons, 'inteiro')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Valor por dia, empilhado por forma. As formas menores se juntam em "Outras" (a paleta é curta). */
const MAX_SERIES = 7;
function PorDia({ d }: { d: PagamentosDoPeriodo }) {
  const cats = d.serie.map((p) => diaCurto(p.dia));
  const principais = d.formas.slice(0, MAX_SERIES);
  const chaves = new Set(principais.map((x) => x.forma));
  const series = principais.map((x, i) => ({
    nome: nomeDe(x),
    cor: CORES[i % CORES.length] ?? '#8e959b',
    valores: d.serie.map((p) => (p.porForma[x.forma] ?? 0) / 100),
  }));
  const resto = d.formas.length - principais.length;
  if (resto > 0)
    series.push({
      nome: 'Outras',
      cor: '#c5cacf',
      valores: d.serie.map((p) =>
        Object.entries(p.porForma)
          .filter(([k]) => !chaves.has(k))
          .reduce((s, [, v]) => s + v / 100, 0),
      ),
    });
  const melhor = d.serie.reduce<(typeof d.serie)[number] | null>(
    (m, p) => (m === null || p.totalCentavos > m.totalCentavos ? p : m),
    null,
  );
  return (
    <CartaoDeGrafico
      id="cv-pagamentos-dia"
      titulo="Pagamentos por dia"
      numero={melhor ? `${brl(d.somaCentavos)} no período` : null}
      conclusao={
        melhor ? (
          <>
            Maior dia: <strong>{diaCurto(melhor.dia)}</strong> ({brl(melhor.totalCentavos)}). Cada
            cor é uma forma de pagamento; dia sem leitura fica sem coluna.
          </>
        ) : (
          'Nenhum dia lido no período.'
        )
      }
      modelos={MODELOS_EMPILHADO}
      padrao="colunas_empilhadas"
      desenhar={(m) => empilhado(m, { cats, series, formato: 'reais' })}
      fonte="Fonte: caixa do HOS (lido dia a dia)"
      vazio={d.serie.length ? null : 'Nenhum dia lido no período.'}
      altura={300}
    />
  );
}
